import React, { useCallback, useEffect } from "react"
import TamCalculatorFilters from "./tam_calculator_filters"
import { TamCalculatorResults } from "./tam_calculator_results"
import type { TamCalculatorFilters as TamCalculatorFiltersType, TamCalculatorProps } from "~/react/types/tam_calculator"
import { useTamCalculatorFilters } from "~/react/hooks/useTamCalculatorFilters"
import { useTamCalculatorResults } from "~/react/hooks/useTamCalculatorResults"
import { TooltipProvider } from "~/react/ui/tooltip"

export default function TamCalculator({ filterProps }: TamCalculatorProps) {
  const { results: initialResults, hasFilters: serverHasFilters } = filterProps

  // Hide skeleton when React mounts
  useEffect(() => {
    const skeleton = document.querySelector(".tam-calculator-skeleton")
    if (skeleton) {
      skeleton.remove()
    }
  }, [])

  // Lift filter state to this component
  const {
    filters,
    updateFilter,
    clearAllFilters: baseClearAllFilters,
    hasFilters,
  } = useTamCalculatorFilters(filterProps.currentFilters)

  // Use the results hook for API-based fetching
  const {
    results,
    isLoading,
    hasFilters: resultsHasFilters,
    acv,
    tam,
    fetchResults,
    clearResults,
  } = useTamCalculatorResults({
    initialResults: initialResults
      ? {
          domains: initialResults.domains || [],
          numberOfCompanies: filterProps.numberOfCompanies,
          tam: filterProps.tam,
        }
      : null,
    initialHasFilters: serverHasFilters,
    initialAcv: filterProps.acv,
    initialTam: filterProps.tam,
  })

  // Wrap clearAllFilters to also clear results
  const clearAllFilters = useCallback(() => {
    baseClearAllFilters()
    clearResults()
  }, [baseClearAllFilters, clearResults])

  // Handle filter changes from TamCalculatorFilters component
  const handleFiltersChange = useCallback(
    (newFilters: TamCalculatorFiltersType) => {
      fetchResults(newFilters)
    },
    [fetchResults],
  )

  return (
    <TooltipProvider>
      <div className="container-fluid">
        <div className="tam-calculator">
          <div className="tam-calculator__layout">
            {/* Filters Sidebar */}
            <aside className="tam-calculator__sidebar">
              <TamCalculatorFilters
                filters={filters}
                filterOptions={filterProps.filterOptions}
                updateFilter={updateFilter}
                clearAllFilters={clearAllFilters}
                hasFilters={hasFilters}
                onFiltersChange={handleFiltersChange}
              />
            </aside>

            {/* Results Area */}
            <main className="tam-calculator__main">
              <TamCalculatorResults
                results={results}
                hasFilters={resultsHasFilters}
                isLoading={isLoading}
                acv={acv}
                tam={tam}
              />
            </main>
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
